import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import ProtectedRoute from "components/ProtectedRoute";
import RedirectProfileEdit from "components/RedirectProfileEdit";
import AudioPlayerWrapper from "components/AudioPlayerWrapper";

// Critical routes - loaded immediately (above-the-fold content)
import LandingPage from './pages/landing-page';
import AuthenticationHub from './pages/authentication-hub';
import NotFound from "pages/NotFound";

// Lazy load all other routes for code splitting
const AdminDashboard = lazy(() => import('./pages/admin-dashboard'));
const NotificationsCenter = lazy(() => import('./pages/notifications-center'));
const EventsHub = lazy(() => import('./pages/events-hub'));
const ForumCategories = lazy(() => import('./pages/forum-categories'));
const UserProfileManagement = lazy(() => import('./pages/user-profile-management'));
const PartnerRegistration = lazy(() => import('./pages/partner-registration'));
const BlogContentCenter = lazy(() => import('./pages/blog-content-center'));
const MessagingSystem = lazy(() => import('./pages/messaging-system'));
const UserDiscovery = lazy(() => import('./pages/user-discovery'));
const TranslationManagement = lazy(() => import('./pages/translation-management'));
const SystemSettings = lazy(() => import('./pages/system-settings'));
const UserManagement = lazy(() => import('./pages/user-management'));
const OpportunitiesListing = lazy(() => import('./pages/opportunities-listing'));
const OpportunityApplicationInterface = lazy(() => import('./pages/opportunity-application-interface'));
const RolePermissionManagement = lazy(() => import('./pages/role-permission-management'));
const CommunityRecognitionHub = lazy(() => import('./pages/community-recognition-hub'));
const ContentModerationDashboard = lazy(() => import('./pages/content-moderation-dashboard'));
const AnalyticsAndReportingDashboard = lazy(() => import('./pages/analytics-and-reporting-dashboard'));
const UserPublicProfile = lazy(() => import('./pages/user-profile-public'));
const ReportSensitiveIssue = lazy(() => import('./pages/report-sensitive-issue'));
const ReportTrack = lazy(() => import('./pages/report-sensitive-issue-track'));
const ReportIssueList = lazy(() => import('./pages/report-issue-list'));
const ForumThread = lazy(() => import('./pages/forum-thread'));
const EventDetail = lazy(() => import('./pages/event-detail'));
const OpportunityDetail = lazy(() => import('./pages/opportunity-detail'));
const OpportunityApplications = lazy(() => import('./pages/opportunity-applications'));
const AuditTrail = lazy(() => import('./pages/audit-trail'));

/** Set VITE_FEATURE_AUDIT_TRAIL=false in .env to temporarily hide Audit Trail (nav + routes). */
const isAuditTrailEnabled = import.meta.env.VITE_FEATURE_AUDIT_TRAIL !== 'false';
const PollsAndSurveysHub = lazy(() => import('./pages/polls-and-surveys-hub'));
const PollDetail = lazy(() => import('./pages/poll-detail'));
const PrivacyPolicy = lazy(() => import('./pages/privacy-policy'));
const TermsOfService = lazy(() => import('./pages/terms-of-service'));
const ContentManager = lazy(() => import('./pages/content-manager'));
const SensitiveIssueReports = lazy(() => import('./pages/sensitive-issue-reports'));
const ConnectPending = lazy(() => import('./pages/connect-pending'));
const ResetPassword = lazy(() => import('./pages/reset-password'));
const OAuthCallback = lazy(() => import('./pages/oauth-callback'));
const Timeline = lazy(() => import('./pages/timeline'));
const TagPage = lazy(() => import('./pages/tag-page'));
const SearchPage = lazy(() => import('./pages/search'));

// Loading fallback component
const RouteLoadingFallback: React.FC = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
      <p className="text-muted-foreground">Loading...</p>
    </div>
  </div>
);

const AppRoutes: React.FC = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <AudioPlayerWrapper />
        <Suspense fallback={<RouteLoadingFallback />}>
          <RouterRoutes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/home" element={<LandingPage />} />
          <Route path="/login" element={<AuthenticationHub />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/auth/google/callback" element={<OAuthCallback />} />
          <Route path="/auth/facebook/callback" element={<OAuthCallback />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          
          {/* Legacy routes - redirect to new structure */}
          <Route path="/landing-page" element={<LandingPage />} />
          <Route path="/authentication-hub" element={<AuthenticationHub />} />
          <Route path="/user-profile-public/:userId" element={<ProtectedRoute><UserPublicProfile /></ProtectedRoute>} />
          
          {/* Protected Routes - Require Authentication */}
          <Route path="/timeline" element={<ProtectedRoute><Timeline /></ProtectedRoute>} />
          <Route path="/search" element={<ProtectedRoute><SearchPage /></ProtectedRoute>} />
          <Route path="/notifications" element={<ProtectedRoute><NotificationsCenter /></ProtectedRoute>} />
          <Route path="/events" element={<EventsHub />} />
          <Route path="/events/:eventSlug" element={<EventDetail />} />
          {/* Forum routes - public read access, auth required for write operations */}
          <Route path="/forums" element={<ForumCategories />} />
          <Route path="/forums/:categorySlug" element={<ForumCategories />} />
          <Route path="/forums/:categorySlug/:threadSlug" element={<ForumThread />} />
          {/* Legacy route - redirect to new format if possible, otherwise redirect to forums */}
          <Route path="/forum/thread/:threadId" element={<ForumThread />} />
          {/* User profile routes - require authentication (profiles are not publicly accessible) */}
          <Route path="/user/:userIdentifier/edit" element={<ProtectedRoute><UserProfileManagement /></ProtectedRoute>} />
          <Route path="/user/:userIdentifier" element={<ProtectedRoute><UserPublicProfile /></ProtectedRoute>} />
          <Route path="/partners/apply" element={<ProtectedRoute><PartnerRegistration /></ProtectedRoute>} />
          <Route path="/blog" element={<BlogContentCenter />} />
          <Route path="/blog/:slug" element={<BlogContentCenter />} />
          <Route path="/tags/:tagName" element={<TagPage />} />
          <Route path="/inbox" element={<ProtectedRoute><MessagingSystem /></ProtectedRoute>} />
          <Route path="/connect" element={<ProtectedRoute><UserDiscovery /></ProtectedRoute>} />
          <Route path="/opportunities" element={<ProtectedRoute><OpportunitiesListing /></ProtectedRoute>} />
          <Route path="/opportunities/:opportunitySlug" element={<OpportunityDetail />} />
          <Route path="/opportunities/:opportunitySlug/apply" element={<ProtectedRoute><OpportunityApplicationInterface /></ProtectedRoute>} />
          <Route path="/opportunities/:opportunitySlug/applications" element={<ProtectedRoute><OpportunityApplications /></ProtectedRoute>} />
          <Route path="/recognition" element={<ProtectedRoute><CommunityRecognitionHub /></ProtectedRoute>} />
          <Route path="/polls" element={<ProtectedRoute><PollsAndSurveysHub /></ProtectedRoute>} />
          <Route path="/polls/:pollId" element={<ProtectedRoute><PollDetail /></ProtectedRoute>} />
          <Route path="/issues" element={<ProtectedRoute><ReportSensitiveIssue /></ProtectedRoute>} />
          <Route path="/report-issue" element={<ProtectedRoute><ReportSensitiveIssue /></ProtectedRoute>} />
          <Route path="/report-issue/track" element={<ProtectedRoute><ReportTrack /></ProtectedRoute>} />
          <Route path="/report-issue/list" element={<ProtectedRoute requireAdmin><ReportIssueList /></ProtectedRoute>} />
          <Route path="/connect/pending" element={<ProtectedRoute><ConnectPending /></ProtectedRoute>} />
          
          {/* Legacy routes - redirect to new structure */}
          <Route path="/dashboard" element={<Navigate to="/timeline" replace />} />
          <Route path="/user-dashboard" element={<Navigate to="/timeline" replace />} />
          <Route path="/user/profile/edit" element={<ProtectedRoute><RedirectProfileEdit /></ProtectedRoute>} />
          <Route path="/notifications-center" element={<ProtectedRoute><NotificationsCenter /></ProtectedRoute>} />
          <Route path="/events-hub" element={<EventsHub />} />
          <Route path="/forum-categories" element={<ProtectedRoute><ForumCategories /></ProtectedRoute>} />
          <Route path="/forum-thread/:threadId" element={<ProtectedRoute><ForumThread /></ProtectedRoute>} />
          <Route path="/user-profile-management" element={<ProtectedRoute><UserProfileManagement /></ProtectedRoute>} />
          <Route path="/user-profile-public/:userId" element={<ProtectedRoute><UserPublicProfile /></ProtectedRoute>} />
          <Route path="/partner-registration" element={<ProtectedRoute><PartnerRegistration /></ProtectedRoute>} />
          <Route path="/blog-content-center" element={<ProtectedRoute><BlogContentCenter /></ProtectedRoute>} />
          <Route path="/messaging-system" element={<ProtectedRoute><MessagingSystem /></ProtectedRoute>} />
          <Route path="/user-discovery" element={<ProtectedRoute><UserDiscovery /></ProtectedRoute>} />
          <Route path="/opportunities-listing" element={<ProtectedRoute><OpportunitiesListing /></ProtectedRoute>} />
          <Route path="/opportunity-application-interface" element={<ProtectedRoute><OpportunityApplicationInterface /></ProtectedRoute>} />
          <Route path="/community-recognition-hub" element={<ProtectedRoute><CommunityRecognitionHub /></ProtectedRoute>} />
          <Route path="/polls-and-surveys-hub" element={<ProtectedRoute><PollsAndSurveysHub /></ProtectedRoute>} />
          <Route path="/report-issue" element={<ProtectedRoute><ReportSensitiveIssue /></ProtectedRoute>} />
          <Route path="/report-sensitive-issue" element={<ProtectedRoute><ReportSensitiveIssue /></ProtectedRoute>} />
          
          {/* Admin Routes - Require Admin Role */}
          {/* Main admin dashboard - accessible at /admin */}
          <Route path="/admin" element={<ProtectedRoute requireAdmin><AdminDashboard /></ProtectedRoute>} />
          {/* Admin sub-pages under /admin/{page-slug} */}
          <Route path="/admin/dashboard" element={<ProtectedRoute requireAdmin><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/users" element={<ProtectedRoute requireAdmin><UserManagement /></ProtectedRoute>} />
          <Route path="/admin/translations" element={<ProtectedRoute requireAdmin><TranslationManagement /></ProtectedRoute>} />
          <Route path="/admin/settings" element={<ProtectedRoute requireSuperAdmin><SystemSettings /></ProtectedRoute>} />
          <Route path="/admin/roles" element={<Navigate to="/admin/settings?tab=roles_permissions" replace />} />
          <Route path="/admin/moderation" element={<ProtectedRoute requireAdmin><ContentModerationDashboard /></ProtectedRoute>} />
          <Route path="/admin/analytics" element={<ProtectedRoute requireAdmin><AnalyticsAndReportingDashboard /></ProtectedRoute>} />
          <Route path="/admin/audit" element={<ProtectedRoute requireAdmin><AuditTrail /></ProtectedRoute>} />
          <Route path="/admin/content-manager" element={<ProtectedRoute requireAdmin><ContentManager /></ProtectedRoute>} />
          <Route path="/admin/sensitive-issue-reports" element={<ProtectedRoute requireAdmin><SensitiveIssueReports /></ProtectedRoute>} />
          
          {/* Legacy routes - redirect to new structure (optional, can be removed later) */}
          <Route path="/admin-dashboard" element={<ProtectedRoute requireAdmin><AdminDashboard /></ProtectedRoute>} />
          <Route path="/user-management" element={<ProtectedRoute requireAdmin><UserManagement /></ProtectedRoute>} />
          <Route path="/translation-management" element={<ProtectedRoute requireAdmin><TranslationManagement /></ProtectedRoute>} />
          <Route path="/system-settings" element={<ProtectedRoute requireSuperAdmin><SystemSettings /></ProtectedRoute>} />
          <Route path="/role-permission-management" element={<Navigate to="/admin/settings?tab=roles_permissions" replace />} />
          <Route path="/content-moderation-dashboard" element={<ProtectedRoute requireAdmin><ContentModerationDashboard /></ProtectedRoute>} />
          <Route path="/analytics-and-reporting-dashboard" element={<ProtectedRoute requireAdmin><AnalyticsAndReportingDashboard /></ProtectedRoute>} />
          <Route path="/audit-trail" element={isAuditTrailEnabled ? <ProtectedRoute requireAdmin><AuditTrail /></ProtectedRoute> : <Navigate to="/admin" replace />} />
          
          <Route path="*" element={<NotFound />} />
          </RouterRoutes>
        </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default AppRoutes;